import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Refreshes the cart drawer content using Shopify's section rendering API
   * This preserves the component state and keeps the drawer open if it was open
   * @returns {Promise<void>}
   */
  async refresh() {
    const wasOpen = this.refs?.dialog?.open || false;

    try {
      // Use section renderer to fetch and morph the cart-drawer section
      // This preserves component state, event listeners, and dialog references
      await sectionRenderer.renderSection('cart-drawer', { cache: false });

      // If the drawer was open before refresh, ensure it stays open
      if (wasOpen && !this.refs?.dialog?.open) {
        this.showDialog();
      }
    } catch (error) {
      console.error('Failed to refresh cart drawer:', error);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
